/**
 * ptキャンペーンの掲示サービス
 * 上部の帯に出す掲示を取得します。セッションは要りません。
 * RPCが未適用のDBでは unavailable を返し、呼び出し側は何も出しません。
 */

import { getSupabaseClient } from '../../services/supabase/client';
import { AppError } from '../utils/errorHandling';
import { logError, logWarn } from '../utils/logger';

/** 掲示を取得するRPC名 */
const VISITOR_POINT_CAMPAIGN_BOARD_RPC_NAME = 'visitor_get_point_campaign_board';

/** 掲示の取得失敗のエラーコード */
export const POINT_CAMPAIGN_BOARD_FETCH_FAILED_CODE = 'POINT_CAMPAIGN_BOARD_FETCH_FAILED';

/** 掲示1件 */
export type PointCampaignBoardItem = {
  id: string;
  /** spend: 景品の必要pt / earn: 企画の獲得pt */
  side: 'spend' | 'earn';
  title: string;
  body: string;
  /** 予告を出し始める時刻（epoch ms）。無ければ開始まで出さない */
  noticeFromMs: number | null;
  startsAtMs: number;
  endsAtMs: number;
};

/** 取得結果。unavailable はRPCが未適用のDB */
export type PointCampaignBoardResult =
  | { kind: 'board'; serverTime: unknown; items: PointCampaignBoardItem[] }
  | { kind: 'unavailable' };

/**
 * RPCエラーが「関数が未適用」を示すか判定する（visitorHomeBootstrapService と同じ基準）
 * @param {{ code?: string, message?: string }} rpcError - RPCエラー
 * @returns {boolean} 未適用ならtrue
 */
const isUndefinedFunctionError = (rpcError: { code?: string; message?: string }): boolean => (
  rpcError.code === 'PGRST202' || (rpcError.message ?? '').includes('Could not find the function')
);

/**
 * wireの1行を検証して掲示にする
 * @param {any} row - wireの1行
 * @returns {PointCampaignBoardItem | null} 掲示。読めない行はnull
 */
const parseBoardItem = (row): PointCampaignBoardItem | null => {
  /** 開始時刻 */
  const startsAtMs = Date.parse(row?.starts_at);
  /** 終了時刻 */
  const endsAtMs = Date.parse(row?.ends_at);
  /** 予告の開始時刻（無ければnull。読めなければNaN） */
  const noticeFromMs = row?.notice_from === null || row?.notice_from === undefined
    ? null
    : Date.parse(row.notice_from);
  if (
    typeof row?.id !== 'string' || row.id === ''
    || (row?.side !== 'spend' && row?.side !== 'earn')
    || typeof row?.title !== 'string' || row.title === ''
    || !Number.isFinite(startsAtMs)
    || !Number.isFinite(endsAtMs)
    || Number.isNaN(noticeFromMs)
  ) {
    return null;
  }
  return {
    id: row.id,
    side: row.side,
    title: row.title,
    body: typeof row.body === 'string' ? row.body : '',
    noticeFromMs,
    startsAtMs,
    endsAtMs,
  };
};

/**
 * 掲示を取得する。読めない行だけを除き、残りは返す
 * @returns {Promise<PointCampaignBoardResult>} 掲示、またはRPCが未適用であること
 * @throws {AppError} 通信失敗・応答の形が不正な場合
 */
export const fetchPointCampaignBoard = async (): Promise<PointCampaignBoardResult> => {
  /** RPC結果 */
  const { data, error } = await getSupabaseClient().rpc(VISITOR_POINT_CAMPAIGN_BOARD_RPC_NAME);

  if (error) {
    if (isUndefinedFunctionError(error)) {
      return { kind: 'unavailable' };
    }
    logError(`${VISITOR_POINT_CAMPAIGN_BOARD_RPC_NAME} の取得に失敗しました。`, {
      code: error.code,
      message: error.message,
    });
    throw new AppError('ptキャンペーンの掲示を取得できませんでした。', {
      code: POINT_CAMPAIGN_BOARD_FETCH_FAILED_CODE,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  if (!Array.isArray(data?.items)) {
    throw new AppError('ptキャンペーンの掲示の形式が不正です。', {
      code: POINT_CAMPAIGN_BOARD_FETCH_FAILED_CODE,
      statusCode: 500,
    });
  }

  /** 検証済みの掲示 */
  const items = data.items.map(parseBoardItem).filter(Boolean) as PointCampaignBoardItem[];
  if (items.length !== data.items.length) {
    logWarn('ptキャンペーンの掲示に読めない行があったため除きました。', {
      received: data.items.length,
      parsed: items.length,
    });
  }
  return { kind: 'board', serverTime: data.server_time, items };
};
